import styled, {
  animatedShadowOnHover,
  BoxShadows,
  breakpoints,
  css,
  Device,
  falconTheme,
  FlattenSimpleInterpolation,
  Icon,
  IFalconTheme,
  Image,
  ResponsiveTypographyVariant,
  Scale,
  StyledComponent,
  typographyVariantProperties,
} from '@se-admin/falcon';
import PriceValue from '../PriceValue';
import { SaleCardVariant } from './SaleCard.interfaces';

interface ISaleCardWrapperProps {
  variant: SaleCardVariant;
}

const SALE_IMAGE_RATIO_PERCENTAGE: number = 69.54;
export const SMALL_SALE_IMAGE_RATIO_PERCENTAGE: number = 66.67;
const URGENT_BADGE_MOBILE_TOP_DISTANCE: number = 6;
const URGENT_BADGE_DESKTOP_TIME_LIMITED_DISTANCE: number = 43;

export const SaleCardWrapper: StyledComponent<'div', IFalconTheme, ISaleCardWrapperProps> = styled(
  'div'
)`
  cursor: pointer;
  position: relative;
  border-radius: ${Scale.xxs};
  box-sizing: border-box;
  ${animatedShadowOnHover(BoxShadows.level2, BoxShadows.level4)}
  ${({ theme }) =>
    `font-family: ${theme.fontFamilyBody.join()};
     background: ${theme.color.white};`};

  ${({ variant }: ISaleCardWrapperProps) =>
    SaleCardVariant.normal !== variant
      ? `
      width: 100%;
    `
      : `
      padding: ${Scale.m};
      margin-bottom: ${Scale.l};
      @media only screen and (min-width: ${breakpoints[Device.tablet]}) {
        padding: ${Scale.l};
        display: -ms-grid;
        display: grid;
        width: 100%;
        -ms-grid-rows: auto auto;
        grid-template-rows: auto auto;
        -ms-grid-columns: minmax(290px, 400px) ${Scale.s} minmax(330px, 1fr);
        grid-template-columns: minmax(290px, 400px) ${Scale.s} minmax(330px, 1fr);
      }
  `}

  &::after {
    content: '';
    clear: both;
    display: table;
  }
`;

export const SaleCardLink: StyledComponent<'a', IFalconTheme, {}> = styled('a')`
  user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  pointer-events: auto;
  text-decoration: none;
  display: flex;
  width: 100%;
  height: 100%;
`;

SaleCardWrapper.defaultProps = { theme: falconTheme };

export const SaleCardContent: StyledComponent<'div', IFalconTheme, {}> = styled('div')`
  padding: ${Scale.m};
`;

// POSITIONING CONTAINERS

interface IImageContainerProps {
  variant: SaleCardVariant;
  theme?: IFalconTheme;
}

export const ImageContainer: StyledComponent<'div', IFalconTheme, IImageContainerProps> = styled(
  'div'
)`
  ${({ variant, theme }: IImageContainerProps) =>
    SaleCardVariant.normal === variant
      ? `
        @media only screen and (min-width: ${breakpoints[Device.tablet]}) {
          display: block;
          height: 0;
          padding-bottom: ${SALE_IMAGE_RATIO_PERCENTAGE}%;
          background-color: ${theme.color.grey20};
        }
      `
      : `
        ::after {
          border-radius: ${Scale.xxs};
          content: '';
          height: 100%;
          width: 100%;
          background: linear-gradient(180deg, ${theme.color.black} 0%, transparent 28.13%);
          opacity: 0.4;
          position: absolute;
        }
        img {
          display: block;
        }
        position: relative;
        height: 0;
        padding-bottom: ${SMALL_SALE_IMAGE_RATIO_PERCENTAGE}%;
      `}
`;

interface IHeartContainerProps {
  variant: SaleCardVariant;
}

export const HeartContainer: StyledComponent<'div', IFalconTheme, IHeartContainerProps> = styled(
  'div'
)`
  position: absolute;
  top: ${Scale.l};
  right: ${Scale.l};

  ${({ variant }: IHeartContainerProps) =>
    SaleCardVariant.normal === variant
      ? `
      @media only screen and (min-width: ${breakpoints[Device.tablet]}) {
        top: 0.625rem;
        right: 0.625rem;
      }
        `
      : `
        top: ${Scale.xs};
        right: ${Scale.s};
        `}
`;

interface IUrgentDiscountContainerProps {
  variant: SaleCardVariant;
  isTimeLimited: boolean;
}

export const UrgentDiscountContainer: StyledComponent<
  'div',
  IFalconTheme,
  IUrgentDiscountContainerProps
> = styled('div')`
  position: absolute;
  top: ${URGENT_BADGE_MOBILE_TOP_DISTANCE}px;
  left: ${Scale.xs};

  ${({ isTimeLimited }: IUrgentDiscountContainerProps) =>
    `
  @media only screen and (min-width: ${breakpoints[Device.tablet]}) {
    top: ${isTimeLimited ? `${URGENT_BADGE_DESKTOP_TIME_LIMITED_DISTANCE}px` : Scale.m};
    left: ${Scale.m};
  }
`}

  ${({ variant }: IUrgentDiscountContainerProps) =>
    SaleCardVariant.normal === variant &&
    `
    @media only screen and (min-width: ${breakpoints[Device.tablet]}) {

    }
  `}
`;

interface IPriceBadgeContainerProps {
  variant: SaleCardVariant;
}

export const PriceBadgeContainer: StyledComponent<
  'div',
  IFalconTheme,
  IPriceBadgeContainerProps
> = styled('div')`
  justify-content: space-between;

  ${({ variant }: IPriceBadgeContainerProps) =>
    SaleCardVariant.normal === variant
      ? `
        @media only screen and (min-width: ${breakpoints[Device.tablet]}) {
          display: flex;
          justify-content: space-between;
          align-items: center;
          white-space: nowrap;
          flex-wrap: wrap;
        }
      `
      : ``}

  ${({ variant }: IPriceBadgeContainerProps) =>
    SaleCardVariant.small === variant &&
    `
    margin-top: ${Scale.xs};
    margin-bottom: 0;

    /* IE11 hack */
    & > div:first-child,.priceValueContainer,.costViewContainer,.leadRateContainer  {
      width: 100%;
    }
  `}
}`;

export const SaleLabelsContainerSmall: StyledComponent<'div', IFalconTheme, {}> = styled('div')`
  position: absolute;
  bottom: ${Scale.m};
  left: ${Scale.m};
`;

export const SaleLabelsContainer: StyledComponent<'div', IFalconTheme, {}> = styled('div')`
  margin-bottom: ${Scale.xs};
  margin-top: ${Scale.xs};
  @media only screen and (min-width: ${breakpoints[Device.tablet]}) {
    display: none;
  }
`;

export const SaleLabelsContainerDesktop: StyledComponent<'div', IFalconTheme, {}> = styled('div')`
  display: none;
  @media only screen and (min-width: ${breakpoints[Device.tablet]}) {
    display: block;
    margin: ${Scale.m} 0;
  }
`;

export const ButtonContainer: StyledComponent<'div', IFalconTheme, {}> = styled('div')`
  display: none;
  @media only screen and (min-width: ${breakpoints[Device.tablet]}) {
    display: flex;
    margin-top: ${Scale.m};
  }
`;

interface IContentContainerProps {
  variant: SaleCardVariant;
}

export const MobileTitleTextWrapper: StyledComponent<'span', IFalconTheme, {}> = styled('span')`
  color: ${({ theme }) => theme.color.black};
`;

export const PriceDiscountBadgeContainer: StyledComponent<'div', IFalconTheme, {}> = styled('div')`
  display: flex;
  justify-content: space-between;
  margin-top: ${Scale.m};
  align-items: center;
`;

export const ContentContainer: StyledComponent<
  'div',
  IFalconTheme,
  IContentContainerProps
> = styled('div')`
  ${({ variant }: IContentContainerProps) =>
    SaleCardVariant.normal === variant &&
    `
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    
    @media only screen and (min-width: ${breakpoints[Device.tablet]}) {
      -ms-grid-row: 2;
      grid-row-start: 2;
      -ms-grid-row-span: 1;
      grid-row-end: 3;
      -ms-grid-column: 3;
      grid-column-start: 3;
      -ms-grid-column-span: 1;
      grid-column-end: 3;
  }`}
`;

interface IImageLabelContainerProps {
  variant: SaleCardVariant;
}

export const ImageLabelContainer: StyledComponent<
  'div',
  IFalconTheme,
  IImageLabelContainerProps
> = styled('div')`
  display: block;
  ${({ variant }: IImageLabelContainerProps) =>
    SaleCardVariant.normal === variant &&
    `@media only screen and (min-width: ${breakpoints[Device.tablet]}) {
      -ms-grid-row: 2;
      grid-row-start: 2;
      -ms-grid-row-span: 1;
      grid-row-end: 3;
      -ms-grid-column: 1;
      grid-column-start: 1;
      -ms-grid-column-span: 1;
      grid-column-end: 2;
  }`}
`;

export const NormalDiscountContainer: StyledComponent<'div', IFalconTheme, {}> = styled('div')`
  flex-shrink: 0;
  margin-left: auto;

  @media only screen and (min-width: ${breakpoints[Device.tablet]}) {
    margin-left: unset;
  }
`;

export const MobileContentContainer: StyledComponent<'div', IFalconTheme, {}> = styled('div')`
  @media only screen and (min-width: ${breakpoints[Device.tablet]}) {
    display: none;
  }
`;

export const endMessageStyle: FlattenSimpleInterpolation = css`
  display: block;
  @media only screen and (min-width: ${breakpoints[Device.tablet]}) {
    -ms-grid-row: 1;
    grid-row-start: 1;
    -ms-grid-row-span: 1;
    grid-row-end: 2;
    -ms-grid-column: 1;
    grid-column-start: 1;
    -ms-grid-column-span: 3;
    grid-column-end: 4;
    text-align: center;
    margin-bottom: 0.625rem;
    && {
      font-size: ${typographyVariantProperties[ResponsiveTypographyVariant.body].desktopFontSize};
    }
  }
`;

export const endMessageSmallStyle: FlattenSimpleInterpolation = css`
  && {
    font-weight: 700;
  }
  position: absolute;
  color: white;
  top: ${Scale.m};
  left: ${Scale.m};
`;

export const EndMessageDesktop: StyledComponent<
  'div',
  IFalconTheme,
  { saleCardVariant: SaleCardVariant }
> = styled('div')`
  ${({ saleCardVariant }: { saleCardVariant: SaleCardVariant } & IFalconTheme) =>
    SaleCardVariant.small === saleCardVariant
      ? `
      && {
        font-weight: 700;
        line-height: 1;
        font-size: ${typographyVariantProperties[ResponsiveTypographyVariant.body].desktopFontSize};
      }
      position:absolute;
      color:white;
      top: ${Scale.m};
      left: ${Scale.m};
      display: inline-flex;
      padding-right: ${Scale.xxxl}`
      : `
      display: none;
      @media only screen and (min-width: ${breakpoints[Device.tablet]}) {
        display: flex;
        -ms-grid-row: 1;
        grid-row-start: 1;
        -ms-grid-row-span: 1;
        grid-row-end: 2;
        -ms-grid-column: 3;
        grid-column-start: 3;
        -ms-grid-column-span: 1;
        grid-column-end: 4;
        text-align: center;
        && {
          margin-bottom: 0.625rem;
          font-size: ${
            typographyVariantProperties[ResponsiveTypographyVariant.body].desktopFontSize
          };

        }
      }`}
`;

export const SaleCardAndHeartWrapper: StyledComponent<'div', {}, {}> = styled('div')`
  position: relative;
  width: 100%;
  height: 100%;
`;

export const CustomSmallImage: StyledComponent<typeof Image, {}, {}> = styled(Image)`
  position: absolute;
  top: 0;
  left: 0;
  border-top-left-radius: ${Scale.xxs};
  border-top-right-radius: ${Scale.xxs};
`;

export const CustomIcon: StyledComponent<typeof Icon, {}, {}> = styled(Icon)`
  margin-right: ${Scale.xxxs};
  flex-shrink: 0;
`;

export const CustomPriceValue: StyledComponent<typeof PriceValue, {}, {}> = styled(PriceValue)`
  margin-right: ${Scale.m};
`;
